import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Gift, Copy, Share2, Sparkles, Users, CalendarCheck2 } from "lucide-react";

/**
 * /referrals — invite friends, earn one free month for every friend who subscribes.
 * Visible in the drawer for teachers + solo learners.
 */
export default function ReferralsPage() {
  const t = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("/me/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  // Always build the share URL from the current browser origin so it matches what the user sees —
  // never trust the backend-supplied link (it's best-effort and can be wrong behind proxies).
  const shareLink = data?.referral_code
    ? `${window.location.origin}/register/solo?ref=${data.referral_code}`
    : "";

  const copy = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(t("Invite link copied"));
    } catch {
      // Older browsers — show the link in a prompt so user can copy manually
      window.prompt(t("Copy your invite link:"), shareLink);
    }
  };

  const share = async () => {
    if (!shareLink) return;
    const text = `${t("Practice with me on Octave 🎼 — grab a free month with my invite:")}\n${shareLink}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Octave", text }); return; } catch { /* cancelled */ }
    }
    // Fallback: mailto
    window.location.href = `mailto:?subject=${encodeURIComponent(t("Try Octave with me"))}&body=${encodeURIComponent(text)}`;
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="referrals-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-3"
             style={{ background: "#fff4cc", color: "#7a5a00" }}>
          <Sparkles className="w-3 h-3" /> {t("Give a month, get a month")}
        </div>
        <h1 className="font-display text-5xl font-bold">{t("Invite musicians. Earn free months.")}</h1>
        <p className="mt-3" style={{ color: "var(--app-text-muted)" }}>
          {t("Every friend who signs up with your link and subscribes earns you 30 free days on your own plan. Unlimited referrals, no cap.")}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile
          icon={Users} tint="#0077B6"
          label={t("Friends who converted")}
          value={data?.referred_count ?? 0}
          hint={t("they finished a paid checkout")}
          testid="stat-referred-count"
        />
        <StatTile
          icon={CalendarCheck2} tint="#2D6A4F"
          label={t("Bonus days earned")}
          value={data?.bonus_days_earned ?? 0}
          hint={`${Math.floor((data?.bonus_days_earned ?? 0) / 30)} ${t("free months so far")}`}
          testid="stat-bonus-days"
        />
        <StatTile
          icon={Gift} tint="#FFB703"
          label={t("Reward per conversion")}
          value={`${data?.reward_days_per_conversion ?? 30}d`}
          hint={t("added straight to your plan")}
          testid="stat-reward-days"
        />
      </div>

      {/* Share card */}
      <div className="rounded-3xl p-6 border-2 mt-6"
           style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}>
        <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--app-text-muted)" }}>
          {t("Your invite link")}
        </div>
        <div className="flex items-center gap-2 rounded-2xl border-2 px-4 py-3"
             style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}>
          <code className="flex-1 truncate text-sm font-mono" data-testid="referral-link">
            {shareLink || t("Loading…")}
          </code>
          <Button onClick={copy} className="btn-pill btn-outline h-9 px-3" data-testid="copy-referral-link">
            <Copy className="w-4 h-4" /> {t("Copy")}
          </Button>
        </div>

        <div className="mt-3 text-xs" style={{ color: "var(--app-text-muted)" }}>
          {t("Or share your code:")} <code className="font-mono font-bold" data-testid="referral-code">{data?.referral_code || "—"}</code>
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          <Button onClick={share} className="btn-pill btn-primary h-11" data-testid="share-referral">
            <Share2 className="w-4 h-4" /> {t("Share invite")}
          </Button>
          <Button onClick={copy} className="btn-pill btn-outline h-11" data-testid="copy-referral-btn">
            <Copy className="w-4 h-4" /> {t("Copy link")}
          </Button>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-6 rounded-3xl p-6" style={{ background: "var(--app-surface-2)" }}>
        <div className="font-display text-xl font-bold mb-3">{t("How it works")}</div>
        <ol className="space-y-2 text-sm">
          <li className="flex gap-3"><Step n={1} /> {t("Share your link or code with other musicians.")}</li>
          <li className="flex gap-3"><Step n={2} /> {t("They sign up for a free trial.")}</li>
          <li className="flex gap-3"><Step n={3} /> {t("When they subscribe, you get 30 free days added to your plan — no cap, invite as many as you want.")}</li>
        </ol>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, tint, label, value, hint, testid }) {
  return (
    <div className="rounded-3xl p-5 border-2" data-testid={testid}
         style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}>
      <span className="flex items-center justify-center rounded-xl w-10 h-10 mb-3"
            style={{ background: tint, color: tint === "#FFB703" ? "#0a0a0a" : "#fff" }}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="text-3xl font-display font-bold">{value}</div>
      <div className="text-sm font-semibold mt-1">{label}</div>
      <div className="text-xs mt-1" style={{ color: "var(--app-text-muted)" }}>{hint}</div>
    </div>
  );
}

function Step({ n }) {
  return (
    <span className="flex items-center justify-center rounded-full w-6 h-6 text-xs font-bold shrink-0"
          style={{ background: "var(--app-primary)", color: "#fff" }} aria-hidden>{n}</span>
  );
}
